import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X, ArrowRight, ArrowLeft, Sparkles, ChevronDown } from 'lucide-react';

const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: '👋 Welcome to PulpitPilot AI!',
    description: "Let's take a quick 30-second tour to help you get started with your AI-powered sermon preparation.",
    highlight: null,
    mobileDescription: "Quick tour of key features!"
  },
  {
    id: 'create-sermon',
    title: '✍️ Create Sermons',
    description: 'Start here to create AI-powered sermons with historical context and theological depth.',
    highlight: '[data-tutorial="create-sermon"]',
    mobileDescription: 'Tap here to create your first sermon'
  },
  {
    id: 'bible',
    title: '📖 Bible Study',
    description: 'Access the complete Bible with multiple translations and AI-powered book introductions.',
    highlight: '[data-tutorial="bible"]',
    mobileDescription: 'Full Bible with translations'
  },
  {
    id: 'study-guide',
    title: '📚 Study Guide',
    description: 'Use the 3-2-3 study system with our AI Companion for comprehensive Bible study.',
    highlight: '[data-tutorial="study-guide"]',
    mobileDescription: 'AI-powered study assistant'
  },
  {
    id: 'community',
    title: '👥 Community',
    description: 'Share sermons, get inspired, and connect with other preachers.',
    highlight: '[data-tutorial="community"]',
    mobileDescription: 'Connect with preachers'
  },
  {
    id: 'account',
    title: '⚙️ Your Account',
    description: 'Manage your profile, sermon style preferences, and subscription settings.',
    highlight: '[data-tutorial="account"]',
    mobileDescription: 'Profile & settings'
  },
  {
    id: 'complete',
    title: "🚀 You're Ready!",
    description: "That's it! You're all set to start creating meaningful, personalized sermons. Happy preaching!",
    highlight: null,
    mobileDescription: 'Start creating sermons now!'
  }
];

export default function AppTutorial({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    scrollToHighlight();
  }, [currentStep]);

  const scrollToHighlight = () => {
    const step = TUTORIAL_STEPS[currentStep];
    if (step.highlight) {
      const element = document.querySelector(step.highlight);
      if (element) {
        // Wait a bit for any animations
        setTimeout(() => {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          });
        }, 100);
      }
    }
  };

  const handleNext = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTutorial();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTutorial = () => {
    localStorage.setItem('tutorial_completed', 'true');
    if (onComplete) onComplete();
  };

  const step = TUTORIAL_STEPS[currentStep];
  const showHighlight = step.highlight !== null;
  const highlightElement = showHighlight ? document.querySelector(step.highlight) : null;

  return (
    <>
      {/* Dark Overlay - tap to continue on mobile */}
      <div 
        className="fixed inset-0 bg-black/70 z-[9998] transition-all duration-300"
        onClick={isMobile && currentStep > 0 && currentStep < TUTORIAL_STEPS.length - 1 ? handleNext : undefined}
      />

      {/* Spotlight Highlight */}
      {showHighlight && highlightElement && (
        <div
          className="fixed z-[9999] pointer-events-none transition-all duration-300"
          style={{
            top: `${highlightElement.getBoundingClientRect().top + window.pageYOffset - 8}px`,
            left: `${highlightElement.getBoundingClientRect().left + window.pageXOffset - 8}px`,
            width: `${highlightElement.getBoundingClientRect().width + 16}px`,
            height: `${highlightElement.getBoundingClientRect().height + 16}px`,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
            borderRadius: '12px',
            border: '3px solid #E5C163',
          }}
        />
      )}

      {/* Animated Arrow Pointing to Feature */}
      {showHighlight && highlightElement && !isMobile && (
        <div
          className="fixed z-[10000] pointer-events-none"
          style={{
            top: `${highlightElement.getBoundingClientRect().top + window.pageYOffset + highlightElement.getBoundingClientRect().height + 8}px`,
            left: `${highlightElement.getBoundingClientRect().left + window.pageXOffset + highlightElement.getBoundingClientRect().width / 2}px`,
            transform: 'translateX(-50%)'
          }}
        >
          <ChevronDown 
            className="w-12 h-12 text-glory-gold animate-bounce" 
            strokeWidth={3}
          />
        </div>
      )}

      {/* Tutorial Card - Fixed at bottom on mobile, floating on desktop */}
      <div className={`fixed z-[10000] transition-all duration-300 ${
        isMobile 
          ? 'bottom-0 left-0 right-0 rounded-t-3xl' 
          : showHighlight
          ? 'bottom-8 left-1/2 -translate-x-1/2'
          : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
      }`}>
        <Card className={`border-4 border-glory-400 shadow-2xl ${
          isMobile ? 'rounded-b-none' : 'rounded-2xl'
        }`}>
          <CardContent className="p-4 sm:p-6">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={completeTutorial}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 z-10"
              aria-label="Skip tutorial"
            >
              <X className="w-5 h-5" />
            </Button>

            <div className="space-y-3 sm:space-y-4 max-w-md mx-auto">
              {/* Icon for welcome/complete screens */}
              {(step.id === 'welcome' || step.id === 'complete') && (
                <div className="flex justify-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-pulpit-100 to-glory-100 rounded-full flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-pulpit-600 animate-pulse" />
                  </div>
                </div>
              )}

              {/* Title */}
              <h3 className="text-xl sm:text-2xl font-bold text-pulpit-900 text-center leading-tight">
                {step.title}
              </h3>

              {/* Description */}
              <p className="text-sm sm:text-base text-gray-700 text-center leading-relaxed px-2">
                {isMobile && step.mobileDescription ? step.mobileDescription : step.description}
              </p>

              {/* Progress Dots */}
              <div className="flex items-center justify-center space-x-2 pt-2">
                {TUTORIAL_STEPS.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentStep(index)}
                    className={`h-2 rounded-full transition-all ${
                      index === currentStep
                        ? 'w-8 bg-pulpit-600'
                        : index < currentStep
                        ? 'w-2 bg-glory-400'
                        : 'w-2 bg-gray-300'
                    }`}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))}
              </div>

              {/* Step Counter */}
              <p className="text-xs text-center text-gray-500">
                Step {currentStep + 1} of {TUTORIAL_STEPS.length}
              </p>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  className="border-pulpit-300 flex-1 sm:flex-initial"
                  size={isMobile ? "sm" : "default"}
                >
                  <ArrowLeft className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Previous</span>
                </Button>

                {currentStep === TUTORIAL_STEPS.length - 1 ? (
                  <Button
                    onClick={completeTutorial}
                    className="bg-gradient-to-r from-pulpit-600 to-pulpit-700 hover:from-pulpit-700 hover:to-pulpit-800 flex-1 font-semibold"
                  >
                    Get Started
                    <Sparkles className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    className="bg-gradient-to-r from-pulpit-600 to-pulpit-700 hover:from-pulpit-700 hover:to-pulpit-800 flex-1 sm:flex-initial font-semibold"
                    size={isMobile ? "sm" : "default"}
                  >
                    <span className="hidden sm:inline">Next</span>
                    <span className="sm:hidden">Next</span>
                    <ArrowRight className="w-4 h-4 sm:ml-2" />
                  </Button>
                )}
              </div>

              {/* Skip Option */}
              {currentStep < TUTORIAL_STEPS.length - 1 && (
                <div className="text-center">
                  <button
                    onClick={completeTutorial}
                    className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Skip tutorial
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tap hint for mobile */}
      {isMobile && currentStep > 0 && currentStep < TUTORIAL_STEPS.length - 1 && (
        <div className="fixed bottom-32 left-0 right-0 z-[9999] text-center pointer-events-none">
          <p className="text-white text-sm bg-black/60 rounded-full px-4 py-2 inline-block">
            Tap anywhere to continue
          </p>
        </div>
      )}
    </>
  );
}
